import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Spinner } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { APIClient, setAuthorization } from "../../helpers/api_helper";
import ToastAlert from "../../Components/Common/ToastAlert";

const Articles = () => {
    const api = useMemo(() => new APIClient(), []);
    setAuthorization(localStorage.getItem("token"));

    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("pending_approval");
    const [expanded, setExpanded] = useState({});
    const [feedbackOpen, setFeedbackOpen] = useState({});
    const [feedbackText, setFeedbackText] = useState({});
    const [page, setPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);

    const statusColor = { pending: "secondary", pending_approval: "warning", approved: "success", rejected: "danger" };

    const fetchArticles = async (p = page) => {
        setLoading(true);
        try {
            const response = await api.get(`v1/content/articles?status=${status}&page=${p}&page_size=10`);
            setArticles(response?.data || []);
            setMaxPage(response?.max_page || 1);
        } catch (e) {
            if (e.message?.includes("503")) {
                ToastAlert("Content engine database unavailable", "warning");
            } else {
                ToastAlert(e.message, "error");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { setPage(1); fetchArticles(1); }, [status]);
    useEffect(() => { fetchArticles(); }, [page]);

    const approve = async (id) => {
        try {
            await api.patch(`v1/content/articles/${id}/approve`);
            fetchArticles();
        } catch (e) { ToastAlert(e.message, "error"); }
    };

    const reject = async (id) => {
        try {
            await api.patch(`v1/content/articles/${id}/reject`);
            fetchArticles();
        } catch (e) { ToastAlert(e.message, "error"); }
    };

    const submitFeedback = async (id) => {
        const text = feedbackText[id] || "";
        if (!text.trim()) return;
        try {
            await api.patch(`v1/content/articles/${id}/feedback`, { feedback: text });
            setFeedbackOpen(prev => ({ ...prev, [id]: false }));
            setFeedbackText(prev => ({ ...prev, [id]: "" }));
            fetchArticles();
        } catch (e) { ToastAlert(e.message, "error"); }
    };

    const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    const toggleFeedback = (id) => setFeedbackOpen(prev => ({ ...prev, [id]: !prev[id] }));

    document.title = "Articles · Horus";

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Articles" pageTitle="Content" />
                    <Row className="mb-3">
                        <Col xs="auto">
                            <Label className="me-2">Status:</Label>
                            {["pending_approval", "approved", "rejected"].map(s => (
                                <Button key={s} size="sm" color={status === s ? "primary" : "light"} className="me-1"
                                    onClick={() => setStatus(s)}>
                                    {s.replace("_", " ")}
                                </Button>
                            ))}
                        </Col>
                    </Row>
                    {loading ? (
                        <div className="text-center py-5"><Spinner color="primary" /></div>
                    ) : articles.length === 0 ? (
                        <p className="text-muted">No articles found.</p>
                    ) : (
                        <>
                            {articles.map(article => (
                                <Card key={article.id} className="mb-3">
                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <Badge color={statusColor[article.status] || "secondary"} className="me-2">{article.status}</Badge>
                                            <strong>{article.title || article.url}</strong>
                                            {article.source_name && <small className="text-muted ms-2">— {article.source_name}</small>}
                                        </div>
                                        <div className="d-flex gap-2">
                                            {article.status === "pending_approval" && (
                                                <>
                                                    <Button size="sm" color="success" onClick={() => approve(article.id)}>Approve</Button>
                                                    <Button size="sm" color="danger" onClick={() => reject(article.id)}>Reject</Button>
                                                    <Button size="sm" color="warning" onClick={() => toggleFeedback(article.id)}>Feedback</Button>
                                                </>
                                            )}
                                            <Button size="sm" color="light" onClick={() => toggleExpand(article.id)}>
                                                {expanded[article.id] ? "Collapse" : "Expand"}
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    {feedbackOpen[article.id] && (
                                        <CardBody className="border-bottom">
                                            <Input
                                                type="textarea"
                                                rows={3}
                                                placeholder="Enter feedback..."
                                                value={feedbackText[article.id] || ""}
                                                onChange={e => setFeedbackText(prev => ({ ...prev, [article.id]: e.target.value }))}
                                            />
                                            <div className="d-flex gap-2 mt-2">
                                                <Button size="sm" color="primary" onClick={() => submitFeedback(article.id)}>Submit</Button>
                                                <Button size="sm" color="light" onClick={() => toggleFeedback(article.id)}>Cancel</Button>
                                            </div>
                                        </CardBody>
                                    )}
                                    {expanded[article.id] && (
                                        <CardBody>
                                            <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>
                                                {article.generated_article || "No generated content."}
                                            </pre>
                                            {article.feedback && <div className="mt-2 p-2 bg-light"><strong>Feedback:</strong> {article.feedback}</div>}
                                        </CardBody>
                                    )}
                                </Card>
                            ))}
                            {maxPage > 1 && (
                                <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                                    <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                                    <span className="text-muted">Page {page} of {maxPage}</span>
                                    <Button size="sm" color="light" disabled={page >= maxPage} onClick={() => setPage(p => p + 1)}>Next</Button>
                                </div>
                            )}
                        </>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Articles;
